<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
import { ref, onMounted, computed, watch } from 'vue'
import { useAuth } from '../../composables/useAuth'
import { getAdvisorProducts, getTaxonomy } from '../../services/api'
import { useRouter } from 'vue-router'
import SupplierLogo from '../../components/SupplierLogo.vue'
import { useCurrentContext } from '../../composables/useCurrentContext'
import type { TaxonomyArea, AdvisorProductListItem } from '../../types/api'

const { advisorId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()
const router = useRouter()
const loading = ref(true)
const products = ref<AdvisorProductListItem[]>([])
const taxonomy = ref<TaxonomyArea[]>([])
const search = ref('')
const selectedArea = ref('')
const selectedSubcategory = ref('')
const page = ref(1)
const perPage = 24

// Debounce search
let timeout: ReturnType<typeof setTimeout> | undefined
const handleSearch = () => {
  loading.value = true
  page.value = 1
  clearTimeout(timeout)
  timeout = setTimeout(() => {
    fetchProducts()
  }, 300)
}

const paginatedProducts = computed(() => {
    const start = (page.value - 1) * perPage
    return products.value.slice(start, start + perPage)
})

const totalPages = computed(() => Math.ceil(products.value.length / perPage))

const fetchProducts = async () => {
    try {
        loading.value = true
        const token = await getAccessTokenSilently()
        
        products.value = await getAdvisorProducts(advisorId.value, { 
            q: search.value,
            areaId: selectedArea.value,
            subcategoryId: selectedSubcategory.value
        }, token)
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

const availableSubcategories = computed(() => {
  if (!selectedArea.value) return []
  const area = taxonomy.value.find(a => a.id === selectedArea.value)
  return area ? area.subcategories : []
})

watch(selectedArea, () => {
    selectedSubcategory.value = ''
    page.value = 1
    fetchProducts()
})

watch(selectedSubcategory, () => {
    page.value = 1
    fetchProducts()
})

onMounted(async () => {
    try {
        // Parallel init
        const taxPromise = getTaxonomy().then(t => taxonomy.value = t);
        const prodPromise = fetchProducts();
        await Promise.all([taxPromise, prodPromise])
    } catch (e) {
        console.error(e)
    }
})

</script>

<template>
  <AppLayout>
    <div class="space-y-6">
      <div class="flex flex-col gap-4">
        <div>
           <h1 class="text-2xl font-bold text-cc-text">Product Directory</h1>
           <p class="text-cc-text-secondary">Browse solutions across the entire Channel Standard ecosystem.</p>
        </div>
        
        <!-- Search and Filter Bar -->
        <div class="bg-cc-elevated p-4 rounded-lg shadow-sm border border-cc-border flex flex-col md:flex-row gap-4 items-center">
            <div class="flex-1 w-full md:w-auto relative">
                 <input v-model="search" @input="handleSearch" type="text" placeholder="Search by name, description..." class="w-full border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none" />
                 <span class="absolute left-3 top-2.5 text-gray-400">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                 </span>
            </div>
            
            <div class="w-full md:w-48">
                 <select v-model="selectedArea" class="w-full border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none">
                     <option value="">All Solutions</option>
                     <option v-for="area in taxonomy" :key="area.id" :value="area.id">{{ area.name }}</option>
                 </select>
            </div>

            <div class="w-full md:w-48">
                 <select v-model="selectedSubcategory" :disabled="!selectedArea" class="w-full border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400">
                     <option value="">All Categories</option>
                     <option v-for="sub in availableSubcategories" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
                 </select>
            </div>
        </div>
      </div>

      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
         <div v-for="i in 6" :key="i" class="bg-gray-100 h-64 rounded-xl"></div>
      </div>
      
      <div v-else-if="products.length === 0" class="text-center py-20 bg-cc-elevated rounded-lg border border-dashed border-cc-border">
         <div class="text-gray-400 dark:text-gray-500 mb-2">No products found</div>
         <p class="text-sm text-cc-text-secondary">Try adjusting your search terms or filters.</p>
      </div>

      <div v-else class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="product in paginatedProducts" :key="product.id" class="group bg-cc-elevated rounded-xl border border-cc-border shadow-sm hover:shadow-md transition flex flex-col pt-6 px-6 pb-6 relative overflow-hidden h-full cursor-pointer" @click="router.push(`/advisor/products/${product.id}`)">
             <!-- Supplier Badge -->
             <div v-if="product.supplier" class="flex items-center gap-3 mb-4 cursor-pointer z-10 relative" @click.stop="router.push(`/advisor/suppliers/${product.supplier.id}`)">
                 <SupplierLogo :src="product.supplier?.logoUrl" :alt="product.supplier?.name" class="w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-700 object-contain border border-gray-100 dark:border-gray-600" />
                 <span class="text-xs font-medium text-cc-text-secondary hover:text-indigo-600 dark:hover:text-indigo-400 transition">{{ product.supplier?.name }}</span>
             </div>

             <div class="mb-4 flex-1">
                 <h3 class="font-bold text-cc-text text-lg mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition">{{ product.name }}</h3>
                 <!-- Prefer marketing summary if available for denser info -->
                 <p class="text-sm text-cc-text-secondary line-clamp-3">{{ product.marketingSummary || product.description }}</p>
             </div>
             
             <div class="flex flex-wrap gap-2 mb-4">
                 <!-- Taxonomy Badges -->
                 <template v-if="product.subcategories && product.subcategories.length">
                     <span v-for="ps in (product.subcategories || []).slice(0, 3)" :key="ps.id" class="text-[10px] bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800 px-2 py-1 rounded uppercase tracking-wide font-medium">
                         {{ ps.name }}
                     </span>
                 </template>
                 <!-- Legacy Fallback -->
                 <template v-else-if="product.categories">
                      <span v-for="cat in (product.categories || []).slice(0, 2)" :key="cat" class="text-[10px] bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 px-2 py-1 rounded text-cc-text-secondary uppercase tracking-wide font-medium">
                         {{ cat }}
                     </span>
                 </template>
             </div>

             <div class="pt-4 border-t border-cc-border flex items-center justify-between text-xs mt-auto">
                 <span class="text-gray-400 dark:text-gray-500">Pricing:</span>
                 <span class="font-medium text-cc-text bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">{{ product.pricingBand || 'Standard' }}</span>
             </div>
        </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex items-center justify-between pt-4 border-t border-cc-border">
            <div class="text-sm text-cc-text-secondary">
                Showing <span class="font-medium">{{ (page - 1) * perPage + 1 }}</span> to <span class="font-medium">{{ Math.min(page * perPage, products.length) }}</span> of <span class="font-medium">{{ products.length }}</span> products
            </div>
            <div class="flex gap-2">
                <button @click="page > 1 && page--" :disabled="page === 1" class="px-3 py-1 border border-cc-border-med rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-white">Previous</button>
                <button @click="page < totalPages && page++" :disabled="page >= totalPages" class="px-3 py-1 border border-cc-border-med rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-white">Next</button>
            </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
